import { fallbackCourtName, venueOfCourt, type EventVenueContext } from './venues';
import { dayKeyInZone } from '@/lib/dayKey';
import { getLuckyLoserPlayerIds } from '@/draw/lucky-loser';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import { tryResolveAppState } from '@/lib/scorecard/resolve';
import { completedGamesWon } from '@/components/match/formatScoreLine';
import { isCompletedGame } from '@/app/[locale]/match/libs/retirement';
import { resolveEventMatchOpponentName } from './eventMatchNames';
import { cardState } from './matchCardStatus';
import { displayMatchCode } from './matchCode';
import { rosterInline, teamEntryKey, type TeamEntry } from './teamLabels';
import type { Database } from '@/types/supabase';

/** One row of `GET /api/event/{id}/matches` (moved here from `EventMatches.tsx`). */
export type EventMatchSchedule = Database['public']['Tables']['event_match_schedule']['Row'] & {
  event_group?: Database['public']['Tables']['event_group']['Row'] | null;
  player_1?: Database['public']['Tables']['player']['Row'] | null;
  player_2?: Database['public']['Tables']['player']['Row'] | null;
  match?: {
    id: string;
    status: string;
    winner_id: string | null;
    is_walkover?: boolean | null;
    app_state?: unknown;
  } | null;
  draw_match?: Array<{
    draw_match_id?: string | null;
    round?: { name: string | null } | null;
    draw?: {
      draw_seeding?: Array<{ player_id: string; is_lucky_loser_slot?: boolean | null }>;
    } | null;
  }> | null;
  rating_data?: {
    player_1: { rating_before: number; rating_after: number; rating_delta: number } | null;
    player_2: { rating_before: number; rating_after: number; rating_delta: number } | null;
  };
  /** `status 'in_progress'` marks a retired match's unfinished game; absent on every other row. */
  game_scores?: Array<{ player1: number; player2: number; status?: string }>;
};

export const UNKNOWN_MATCH_VENUE = '__unknown__';
export type MatchFilterStatus = 'upcoming' | 'live' | 'finished';

export type RowState = 'live' | 'upcoming' | 'finished' | 'winnerless';

export interface MatchSideVM {
  /** Real entrant identity; placeholder seats remain unlinked. */
  playerId?: string | null;
  name: string;
  /** Team roster, verbatim, one line; null off team divisions. */
  roster: string | null;
  rating: number | null;
  /** Authoritative completed-match history only; absent on older row fixtures. */
  ratingDelta?: number | null;
  /** Profile slug, or null when the side has no profile page (team, TBD). */
  username: string | null;
  won: boolean;
}

export interface MatchRowVM {
  id: string;
  matchId: string | null;
  /** The match ID players see on the draw (A1, QUAL-B2); null outside a draw. */
  code: string | null;
  startIso: string;
  court: string;
  /** Structured court/venue metadata for grouped public rows; full `court` remains the print label. */
  courtId?: string | null;
  courtName?: string;
  venueId?: string | null;
  venueName?: string | null;
  divisionId: string | null;
  divisionName: string;
  divisionSortOrder: number;
  state: RowState;
  outcome: string | null;
  isWalkover: boolean;
  sides: [MatchSideVM, MatchSideVM];
  /** `partial` marks a retired match's unfinished game: shown as played, never counted as won. */
  games: { p1: number; p2: number; partial?: boolean }[];
  /** A retirement decided the match (outcome 'retired'): scored, with a winner, marker shown. */
  retired?: boolean;
  gamesWon: { p1: number; p2: number } | null;
  hasScorecard: boolean;
  /** Team division: no personal ratings or profiles. */
  hideRating: boolean;
  /** Lower-cased, accent-stripped text the search box matches against: the names the row displays. */
  searchText: string;
}

export interface RowContext {
  isJapanese: boolean;
  luckyLoserLabel: string;
  luckyLoserPlayerIdsByGroup: Record<string, string[]>;
  teamEntries: Record<string, TeamEntry>;
  courtLabel: (courtId: string | null) => string;
  venueContext?: EventVenueContext;
}

/** Lower-case, strip accents, collapse whitespace: the search normaliser. */
function fold(s: string): string {
  return s.normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

type RawPlayer = NonNullable<EventMatchSchedule['player_1']>;

export function buildMatchRow(m: EventMatchSchedule, ctx: RowContext): MatchRowVM {
  const groupId = m.event_group_id;
  const isTeamDivision = !!m.event_group?.is_team;
  const drawMatch = Array.isArray(m.draw_match) ? m.draw_match[0] : m.draw_match;
  const placeholderIds = getLuckyLoserPlayerIds(drawMatch?.draw?.draw_seeding);
  const assigned = new Set(groupId ? (ctx.luckyLoserPlayerIdsByGroup[groupId] ?? []) : []);

  const status = m.match?.status;
  const winnerless = isWinnerlessRow({ outcome: m.outcome, is_walkover: m.is_walkover });
  const completed = status === 'completed';
  const state: RowState = cardState(
    winnerless,
    status === 'in_progress' ? 'live' : completed ? 'finished' : 'upcoming',
  );

  // Games: completed games from the API; while live, the scorer blob's games.
  let games: { p1: number; p2: number; partial?: boolean }[] = [];
  const retired = m.outcome === 'retired';
  let gamesWon: { p1: number; p2: number } | null = null;
  if (state === 'live') {
    const resolved = tryResolveAppState(m.match?.app_state);
    if (resolved && resolved.games.length > 0) {
      games = resolved.games.map((g) => ({ p1: g.player_1_score, p2: g.player_2_score }));
      gamesWon = { p1: resolved.player_1_games, p2: resolved.player_2_games };
    }
  } else if (state === 'finished' && !m.is_walkover && (m.game_scores?.length ?? 0) > 0) {
    games = m.game_scores!.map((g) => ({
      p1: g.player1,
      p2: g.player2,
      ...(isCompletedGame(g) ? {} : { partial: true }),
    }));
    const won = completedGamesWon(m.game_scores!);
    gamesWon = { p1: won.player1, p2: won.player2 };
  }

  const side = (
    player: RawPlayer | null | undefined,
    rating: { rating_after: number; rating_delta: number } | null | undefined,
  ): MatchSideVM => {
    if (!player)
      return {
        name: '',
        roster: null,
        rating: null,
        ratingDelta: null,
        username: null,
        won: false,
      };
    const entry = groupId ? ctx.teamEntries[teamEntryKey(groupId, player.id)] : undefined;
    const name =
      resolveEventMatchOpponentName({
        player,
        isLuckyLoserPlaceholder: placeholderIds.has(player.id),
        luckyLoserLabel: ctx.luckyLoserLabel,
        entry,
        isAssignedLuckyLoser: assigned.has(player.id),
        isJapanese: ctx.isJapanese,
      }) ?? '';
    return {
      playerId: placeholderIds.has(player.id) ? null : player.id,
      name,
      roster: rosterInline(entry?.roster),
      rating:
        completed && rating && Number.isFinite(rating.rating_after)
          ? rating.rating_after
          : Number.isFinite(player.current_rating)
            ? player.current_rating
            : null,
      ratingDelta:
        completed &&
        !winnerless &&
        !m.is_walkover &&
        !m.match?.is_walkover &&
        !isTeamDivision &&
        (m.outcome == null || m.outcome === 'played' || m.outcome === 'retired') &&
        rating &&
        Number.isFinite(rating.rating_after) &&
        Number.isFinite(rating.rating_delta)
          ? rating.rating_delta
          : null,
      username: isTeamDivision || placeholderIds.has(player.id) ? null : (player.username ?? null),
      won: completed && !!m.match?.winner_id && m.match.winner_id === player.id,
    };
  };

  const sides: [MatchSideVM, MatchSideVM] = [
    side(m.player_1, m.rating_data?.player_1),
    side(m.player_2, m.rating_data?.player_2),
  ];

  // Only what the row displays: a player with an alias is findable by the alias
  // alone, so a real name the row hides must never match (and so reveal) them.
  const searchText = fold(sides.map((s) => `${s.name} ${s.roster ?? ''}`).join(' '));

  const court = ctx.venueContext?.courts.find((c) => c.id === m.court_id);
  const venue = ctx.venueContext ? venueOfCourt(ctx.venueContext, m.court_id) : null;

  return {
    id: m.id,
    matchId: m.match?.id ?? null,
    code: displayMatchCode(drawMatch?.draw_match_id),
    startIso: m.scheduled_datetime,
    court: ctx.courtLabel(m.court_id),
    ...(ctx.venueContext && {
      courtId: m.court_id,
      courtName: court?.name ?? (m.court_id ? fallbackCourtName(m.court_id) : ''),
      venueId: venue?.id ?? null,
      venueName: venue?.name ?? null,
    }),
    divisionId: m.event_group?.id ?? null,
    divisionName: m.event_group?.name ?? '',
    divisionSortOrder: m.event_group?.sort_order ?? 999,
    state,
    outcome: m.outcome ?? null,
    isWalkover: !!m.is_walkover,
    retired,
    sides,
    games,
    gamesWon,
    hasScorecard: !!m.match?.id && (state === 'live' || (state === 'finished' && games.length > 0)),
    hideRating: isTeamDivision,
    searchText,
  };
}

/** Start time, then court (numeric-aware, so "Court 2" precedes "Court 10"), then id. */
export function sortRows(rows: MatchRowVM[]): MatchRowVM[] {
  return [...rows].sort(
    (a, b) =>
      Date.parse(a.startIso) - Date.parse(b.startIso) ||
      a.court.localeCompare(b.court, undefined, { numeric: true }) ||
      a.id.localeCompare(b.id),
  );
}

/** Divisions present in `rows`, in the organiser's order. */
export function divisionsOf(rows: MatchRowVM[]): { id: string; name: string }[] {
  const seen = new Map<string, { name: string; order: number }>();
  for (const r of rows) {
    if (r.divisionId && r.divisionName) {
      seen.set(r.divisionId, { name: r.divisionName, order: r.divisionSortOrder });
    }
  }
  return [...seen.entries()]
    .sort((a, b) => a[1].order - b[1].order)
    .map(([id, { name }]) => ({ id, name }));
}

/**
 * Division chip and player search, applied together. Every whitespace-separated
 * token of the query must appear somewhere in either side's names (or team
 * roster), so "ashby carden" finds that match and "jose" finds "José".
 */
export function filterRows(
  rows: MatchRowVM[],
  f: { divisionId: string; query: string; venueId?: string; status?: MatchFilterStatus },
): MatchRowVM[] {
  const tokens = fold(f.query).split(' ').filter(Boolean);
  return rows.filter(
    (r) =>
      (f.divisionId === 'all' || r.divisionId === f.divisionId) &&
      (f.venueId === undefined || (r.venueId ?? UNKNOWN_MATCH_VENUE) === f.venueId) &&
      (f.status === undefined || (r.state === 'winnerless' ? 'finished' : r.state) === f.status) &&
      tokens.every((t) => r.searchText.includes(t)),
  );
}

export function dayKeysOf(rows: MatchRowVM[], zone: string): string[] {
  return [...new Set(rows.map((r) => dayKeyInZone(r.startIso, zone)))].sort();
}

export function groupByDay(
  rows: MatchRowVM[],
  zone: string,
): { day: string; rows: MatchRowVM[] }[] {
  const byDay = new Map<string, MatchRowVM[]>();
  for (const r of rows) {
    const day = dayKeyInZone(r.startIso, zone);
    byDay.set(day, [...(byDay.get(day) ?? []), r]);
  }
  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, rs]) => ({ day, rows: rs }));
}

/** Concurrent matches share one time heading. `rows` must already be sorted. */
export function groupByStart(rows: MatchRowVM[]): { startIso: string; rows: MatchRowVM[] }[] {
  const groups: { startIso: string; rows: MatchRowVM[] }[] = [];
  for (const r of rows) {
    const last = groups[groups.length - 1];
    if (last && last.startIso === r.startIso) last.rows.push(r);
    else groups.push({ startIso: r.startIso, rows: [r] });
  }
  return groups;
}

/** Within one start, preserve first-seen venue order and court row order. Names are not identities. */
export function groupByVenue(rows: MatchRowVM[]): {
  venueId: string | null;
  venueName: string | null;
  rows: MatchRowVM[];
}[] {
  const groups = new Map<
    string | null,
    { venueId: string | null; venueName: string | null; rows: MatchRowVM[] }
  >();
  for (const row of rows) {
    const venueId = row.venueId ?? null;
    const group = groups.get(venueId);
    if (group) group.rows.push(row);
    else groups.set(venueId, { venueId, venueName: row.venueName ?? null, rows: [row] });
  }
  return [...groups.values()];
}
